import { useState } from "react";
import { ZoomIn, ImagePlus } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Reveal } from "./Reveal";

// Slot immagini reali: posiziona i file in /app/frontend/public/previews/
const previews = [
    {
        id: "quesiti",
        label: "Pagina del test",
        color: "#1D4E9E",
        testId: "pdf-preview-tab-quesiti",
        src: "/previews/pagina-test.png",
    },
    {
        id: "scheda",
        label: "Scheda risposte",
        color: "#C0392B",
        testId: "pdf-preview-tab-scheda",
        src: "/previews/scheda-risposte.png",
    },
    {
        id: "correzioni",
        label: "Correzioni commentate",
        color: "#1E8449",
        testId: "pdf-preview-tab-correzioni",
        src: "/previews/correzioni-commentate.png",
    },
];

const SlotImage = ({ src, label, large = false }) => {
    const [error, setError] = useState(false);
    if (error) {
        return (
            <div
                className={`flex w-full flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 text-center ${
                    large ? "aspect-[3/4]" : "h-full"
                }`}
            >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy-950/5">
                    <ImagePlus className="h-6 w-6 text-slate-400" />
                </span>
                <p className="px-6 text-xs font-semibold text-navy-950">Immagine in arrivo</p>
                <p className="px-6 text-[10px] leading-relaxed text-slate-400">
                    Qui verrà mostrata la pagina reale «{label}» del PDF
                </p>
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={`Anteprima reale: ${label} — Difesa AMM 2026, 3 Simulazioni Complete`}
            onError={() => setError(true)}
            loading="lazy"
            className={`w-full rounded-md ${large ? "max-h-[85vh] object-contain" : "h-full object-contain"}`}
        />
    );
};

export const Preview = () => (
    <section id="anteprima" className="relative bg-slate-50 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <Reveal className="text-center">
                <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy-950 sm:text-4xl lg:text-5xl">
                    Guarda cosa trovi all'interno
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-base text-slate-500">
                    Ecco alcune anteprime reali del materiale che riceverai.
                </p>
            </Reveal>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {previews.map((p, i) => (
                    <Reveal key={p.id} delay={i * 0.08}>
                        <Dialog>
                            <DialogTrigger asChild>
                                <button
                                    data-testid={i === 0 ? "pdf-preview-zoom-trigger" : `pdf-preview-card-${p.id}`}
                                    className="group relative block w-full rounded-xl border border-slate-200 bg-white p-4 pt-8 shadow-sm transition-[box-shadow,transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-[0_22px_50px_rgba(10,30,60,0.12)]"
                                    aria-label={`Ingrandisci anteprima: ${p.label}`}
                                >
                                    <span
                                        data-testid={p.testId}
                                        className="absolute -top-3 left-4 rounded-md px-3 py-1 font-metric text-[10px] font-bold tracking-widest text-white"
                                        style={{ background: p.color }}
                                    >
                                        {p.label.toUpperCase()}
                                    </span>
                                    <div className="aspect-[510/744] w-full overflow-hidden">
                                        <SlotImage src={p.src} label={p.label} />
                                    </div>
                                    <span className="absolute bottom-6 right-6 flex h-9 w-9 items-center justify-center rounded-full bg-navy-950/85 text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                        <ZoomIn className="h-4 w-4" />
                                    </span>
                                </button>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl border-slate-200 bg-white p-3 sm:p-5">
                                <SlotImage src={p.src} label={p.label} large />
                            </DialogContent>
                        </Dialog>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
